import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useFeatureIsOn } from '@growthbook/growthbook-react';

import { FEATURE_FLAG_KEYS } from '../../config/growthbook';
import { getInsightById } from '../insights/insights.service';
import { getInsights } from '../insights/InsightsService';
import {
  ActiveTab,
  type SpotlightInsight,
} from '../insights/spotlight-details/spotlightDetails.types';
import {
  fetchCustomerSpotlightCount,
  fetchCustomerSpotlightsList,
  fetchLegacySpotlightInsights,
  getCustomerSpotlightById,
  normalizeLegacyVendorAdvisoryLinks,
} from './customer-spotlight.service';
import { adaptCustomerSpotlightToInsight } from './spotlight.adapt';
import type { CustomerSpotlightsTableQuery } from './spotlights.types';

export function useCustomerSpotlightsFetcher(params: {
  readonly accountId: string;
}) {
  const { accountId } = params;
  const isSpotlightV2PgSource = useFeatureIsOn(
    FEATURE_FLAG_KEYS.SPOTLIGHT_V2_PG_SOURCE
  );

  return useCallback(
    (tableQuery: CustomerSpotlightsTableQuery = {}) => {
      if (isSpotlightV2PgSource) {
        if (!accountId) {
          return Promise.resolve({ data: [], count: 0 });
        }

        return fetchCustomerSpotlightsList(accountId, tableQuery);
      }

      return fetchLegacySpotlightInsights(accountId, tableQuery);
    },
    [accountId, isSpotlightV2PgSource]
  );
}

export function useCustomerSpotlightById(spotlightId: string | undefined) {
  const isSpotlightV2PgSource = useFeatureIsOn(
    FEATURE_FLAG_KEYS.SPOTLIGHT_V2_PG_SOURCE
  );

  const queryKey = isSpotlightV2PgSource
    ? ['customer-spotlight', { spotlightId }]
    : ['spotlight', { spotlightId }];

  const query = useQuery<SpotlightInsight>({
    queryKey,
    queryFn: () => {
      if (!spotlightId) {
        throw new Error('Spotlight id is required');
      }

      if (isSpotlightV2PgSource) {
        return getCustomerSpotlightById(spotlightId).then((response) =>
          adaptCustomerSpotlightToInsight(response.data)
        );
      }

      return getInsightById(spotlightId).then(
        (response) =>
          normalizeLegacyVendorAdvisoryLinks(
            response.data as Record<string, unknown>
          ) as unknown as SpotlightInsight
      );
    },
    enabled: !!spotlightId,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    cacheTime: 0,
    staleTime: 0,
  });

  return { query, queryKey };
}

export function useSpotlightCount(params: {
  readonly accountId: string;
}): number | null {
  const { accountId } = params;
  const isSpotlightV2PgSource = useFeatureIsOn(
    FEATURE_FLAG_KEYS.SPOTLIGHT_V2_PG_SOURCE
  );

  const query = useQuery({
    queryKey: [
      'spotlight-count',
      { accountId, source: isSpotlightV2PgSource ? 'pg' : 'es' },
    ],
    queryFn: () => {
      if (isSpotlightV2PgSource) {
        return fetchCustomerSpotlightCount(accountId);
      }

      return getInsights({
        accountId,
        recommendationTab: ActiveTab.SPOTLIGHT,
      }).then((response) => response.data.totalElements);
    },
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    enabled: !!accountId,
  });

  return query.data ?? null;
}
